import type { Commands } from '@tiptap/core';
import React, { useMemo, useState } from 'react';

import { useRichTextEditor } from '../hooks';
import { useRteUi } from '../ui/ui-context';

// Table operations supported by this menu (matching the reference component).
type TableOperation = Extract<
  keyof Commands['table'],
  | 'addColumnBefore'
  | 'addColumnAfter'
  | 'addRowBefore'
  | 'addRowAfter'
  | 'deleteTable'
  | 'toggleHeaderRow'
  | 'toggleHeaderColumn'
  | 'deleteColumn'
  | 'deleteRow'
>;

/**
 * Props contract expected by the UI adapter's TableCellMenu component.
 * This keeps the shared component headless: we only provide state and actions.
 */
export interface HeadlessTableCellMenuUiProps {
  isOpen: boolean;
  onOpen: (event: React.MouseEvent<HTMLElement>) => void;
  onClose: () => void;
  onSelect: (key: TableOperation) => void;
  items: { key: TableOperation; label: string }[];
  className?: string;
}

/**
 * TableCellMenu (Headless)
 *
 * - Provides the logic to operate on the Tiptap editor's table commands.
 * - Delegates actual UI to an adapter-provided component at ui.components.TableCellMenu.
 * - If no adapter UI is present, renders null.
 */
export default function TableCellMenu() {
  const { editor } = useRichTextEditor();
  const ui = useRteUi();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // If no editor instance is present, nothing to render.
  if (!editor) return null;

  // Adapter-provided UI component (optional).
  // We intentionally loosen typing here to avoid changing the shared UI types contract.
  const TableCellMenuUi = (
    ui as unknown as {
      components: { TableCellMenu?: React.ComponentType<HeadlessTableCellMenuUiProps> };
    }
  ).components.TableCellMenu;

  // If the adapter doesn't provide a concrete UI, keep it headless (no-op render).
  if (!TableCellMenuUi) return null;

  const isOpen = anchorEl !== null;

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleTableOperation = (operation: TableOperation) => {
    editor.chain().focus()[operation]().run();
    handleClose();
  };

  const items = useMemo(
    () =>
      [
        { key: 'toggleHeaderRow', label: 'Toggle Header Row' },
        { key: 'toggleHeaderColumn', label: 'Toggle Header Column' },
        { key: 'addColumnBefore', label: 'Insert Column Before' },
        { key: 'addColumnAfter', label: 'Insert Column After' },
        { key: 'addRowBefore', label: 'Insert Row Before' },
        { key: 'addRowAfter', label: 'Insert Row After' },
        { key: 'deleteTable', label: 'Delete Table' },
        { key: 'deleteColumn', label: 'Delete Column' },
        { key: 'deleteRow', label: 'Delete Row' },
      ] satisfies { key: TableOperation; label: string }[],
    [],
  );

  // We pass a synthetic onClose through item selection by reusing handleTableOperation.
  // Adapters should call onClose when users dismiss the popover.
  return (
    <TableCellMenuUi
      isOpen={isOpen}
      onOpen={handleOpen}
      onClose={handleClose}
      onSelect={handleTableOperation}
      items={items}
      className='table-cell-menu'
    />
  );
}

/**
 * Optional utility for adapters:
 * If you prefer, you can use the following handler to wire item selection:
 *
 *   const { onItemSelect } = useHeadlessTableCellMenuActions();
 *   <MenuItem onClick={() => onItemSelect(item.key)}>{item.label}</MenuItem>
 */
export function useHeadlessTableCellMenuActions() {
  const { editor } = useRichTextEditor();
  const onItemSelect = (operation: TableOperation) => {
    editor?.chain().focus()[operation]().run();
  };
  return { onItemSelect };
}
